<template>
  <div class="staff-panel">

    <!-- TOP BAR -->
    <header class="sp-topbar">
      <div class="sp-topbar-inner">
        <div class="sp-brand">
          <div class="sp-brand-mark">T</div>
          <div class="sp-brand-text">
            <b>Tyng &amp; Co</b>
            <span>{{ isAdminView ? 'Admin Console' : 'Staff Portal' }}</span>
          </div>
        </div>
        <div v-if="isAdminView" class="sp-topbar-crumbs">
          <span class="sp-crumb-link" @click="goBack">User Management</span>
          <fa-icon :icon="['fas', 'chevron-right']" class="sp-crumb-sep" />
          <span class="sp-crumb-active">{{ profile ? profile.full_name : 'Staff' }}</span>
        </div>
        <div class="sp-topbar-actions">
          <button v-if="isAdminView" class="sp-back-btn" @click="goBack">
            <fa-icon :icon="['fas', 'arrow-left']" /> Back
          </button>
          <button v-else class="sp-logout-btn" @click="showLogoutModal = true">
            <fa-icon :icon="['fas', 'sign-out-alt']" /> Logout
          </button>
        </div>
      </div>
    </header>

    <main class="sp-container">

      <div v-if="loading" class="sp-loading">
        <fa-icon :icon="['fas', 'spinner']" spin />
        Loading...
      </div>

      <template v-else-if="profile">

        <!-- ── LEAVE BANNER (both views) ─────────────────── -->
        <div v-if="bannerLeaves.length" class="sp-leave-banner">
          <div class="sp-leave-banner-icon"><fa-icon :icon="['fas', 'umbrella-beach']" /></div>
          <div class="sp-leave-banner-body">
            <span class="sp-leave-banner-title">
              {{ isAdminView ? profile.full_name + "'s Leave" : 'Leave Notice' }}
            </span>
            <div v-for="l in bannerLeaves" :key="l.id" class="sp-leave-banner-item">
              <span :class="['sp-leave-banner-chip', serverDate >= l.start_date ? 'sp-leave-banner-chip--active' : 'sp-leave-banner-chip--upcoming']">
                {{ serverDate >= l.start_date ? 'On Leave' : 'Upcoming' }}
              </span>
              <span class="sp-leave-banner-dates">{{ formatDate(l.start_date) }} – {{ formatDate(l.end_date) }}</span>
              <span v-if="l.note" class="sp-leave-banner-note">{{ l.note }}</span>
            </div>
          </div>
        </div>

        <!-- ── WELCOME SECTION ──────────────────────────────── -->
        <div v-if="!isAdminView" class="sp-welcome">
          <div class="sp-avatar-large">{{ initials }}</div>
          <div class="sp-welcome-text">
            <h1>Welcome, {{ profile.full_name }}</h1>
            <p class="sp-role-tag"><fa-icon :icon="['fas', 'id-badge']" /> Staff</p>
          </div>
        </div>

        <div v-else class="sp-admin-banner">
          <!-- <div class="sp-avatar-large sp-avatar-large--admin">{{ initials }}</div> -->
          <div class="sp-admin-banner-info">
            <h1>{{ profile.full_name }}</h1>
            <div class="sp-admin-banner-meta">
              <span><fa-icon :icon="['fas', 'user']" /> {{ profile.username }}</span>
              <span><fa-icon :icon="['fas', 'calendar-alt']" /> Joined {{ formatDate(profile.created_at) }}</span>
              <span :class="['sp-badge', profile.status === 'active' ? 'sp-badge--green' : 'sp-badge--grey']">{{ profile.status }}</span>
            </div>
            <div class="sp-admin-task-stats" v-if="tasks.length">
              <span class="sp-stat-pill sp-stat-pill--total">{{ tasks.length }} task{{ tasks.length !== 1 ? 's' : '' }}</span>
              <span v-if="activeTasks.length" class="sp-stat-pill sp-stat-pill--active">{{ activeTasks.length }} active</span>
              <span v-if="closedTasks.length" class="sp-stat-pill sp-stat-pill--closed">{{ closedTasks.length }} closed</span>
            </div>
          </div>
        </div>

        <!-- ── PROFILE SECTION ──────────────────────────────── -->
        <div class="sp-card" :class="{ 'sp-card--collapsed': profileCollapsed }">
          <div class="sp-card-header sp-card-header--toggle" @click="profileCollapsed = !profileCollapsed">
            <fa-icon :icon="['fas', 'user-circle']" />
            <span class="sp-card-title">{{ isAdminView ? 'Staff Profile' : 'My Profile' }}</span>
            <fa-icon :icon="['fas', profileCollapsed ? 'chevron-down' : 'chevron-up']" class="sp-card-chevron" />
          </div>

          <transition :css="false" @before-enter="collapseBeforeEnter" @enter="collapseEnter" @leave="collapseLeave">
          <div v-show="!profileCollapsed">

          <!-- View Mode -->
          <div v-if="!editingProfile" class="sp-profile-grid">
            <div class="sp-profile-item">
              <span class="sp-label">Full Name</span>
              <span class="sp-value">{{ profile.full_name }}</span>
            </div>
            <div class="sp-profile-item sp-profile-item-username">
              <span class="sp-label">Username</span>
              <span class="sp-value"><code>{{ profile.username }}</code></span>
            </div>
            <div class="sp-profile-item" v-if="isAdminView">
              <span class="sp-label">IC Number</span>
              <span class="sp-value">{{ profile.ic_number || '—' }}</span>
            </div>
            <div class="sp-profile-item">
              <span class="sp-label">Phone</span>
              <span class="sp-value">{{ profile.phone_number ? (profile.phone_country ? profile.phone_country + ' ' + profile.phone_number : profile.phone_number) : '—' }}</span>
            </div>
            <div class="sp-profile-item">
              <span class="sp-label">Email</span>
              <span class="sp-value">{{ profile.email || '—' }}</span>
            </div>
            <div class="sp-profile-item">
              <span class="sp-label">Bank</span>
              <span class="sp-value">{{ profile.bank_name || '—' }}</span>
            </div>
            <div class="sp-profile-item">
              <span class="sp-label">Bank Account</span>
              <span class="sp-value">{{ profile.bank_account || '—' }}</span>
            </div>
            <div class="sp-profile-item">
              <span class="sp-label">Member Since</span>
              <span class="sp-value">{{ formatDate(profile.created_at) }}</span>
            </div>
            <div class="sp-profile-item">
              <span class="sp-label">Account Status</span>
              <span class="sp-value">
                <span :class="['sp-badge', profile.status === 'active' ? 'sp-badge--green' : 'sp-badge--grey']">{{ profile.status }}</span>
              </span>
            </div>
          </div>
          <div v-if="!editingProfile" class="sp-edit-btn-wrapper">
            <button type="button" class="sp-btn sp-btn-primary" @click="startEditProfile">
              <fa-icon :icon="['fas', 'pen']" /> Edit Profile
            </button>
          </div>

          <!-- Edit Mode -->
          <form v-else class="sp-edit-form" @submit.prevent="submitProfile" novalidate>
            <div class="sp-form-grid">
              <div>
                <text-input v-model="profileForm.full_name" label="Full Name" :required="true" :icon="['fas', 'user']" />
                <p v-if="profileErrors.full_name" class="ap-field-error">{{ profileErrors.full_name }}</p>
              </div>
              <div v-if="isAdminView">
                <text-input v-model="profileForm.username" label="Username" :required="true" :icon="['fas', 'user']" />
                <p v-if="profileErrors.username" class="ap-field-error">{{ profileErrors.username }}</p>
              </div>
              <text-input v-if="isAdminView" v-model="profileForm.ic_number" label="IC Number" :icon="['fas', 'id-card']" />
              <email-input v-model="profileForm.email" label="Email" />
              <phone-input v-model="profileForm.phone" label="Phone" />
              <select-input v-model="profileForm.bank_name" label="Bank Name" :options="bankOptions" placeholder="Select bank…" />
              <text-input v-model="profileForm.bank_account" label="Bank Account" :icon="['fas', 'landmark']" />
              <div v-if="isAdminView">
                <text-input v-model="profileForm.password" label="New Password" type="password" :icon="['fas', 'key']" placeholder="Leave blank to keep current" />
                <p v-if="profileErrors.password" class="ap-field-error">{{ profileErrors.password }}</p>
              </div>
              <select-input
                v-if="isAdminView"
                v-model="profileForm.status"
                label="Status"
                :options="[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]"
              />
            </div>
            <div class="sp-edit-actions">
              <button type="submit" class="sp-btn sp-btn-primary" :disabled="profileSaving">
                <fa-icon v-if="profileSaving" :icon="['fas', 'spinner']" spin /> {{ profileSaving ? 'Saving…' : 'Save Changes' }}
              </button>
              <button type="button" class="sp-btn sp-btn-ghost" @click="editingProfile = false">Cancel</button>
            </div>
          </form>

          <!-- Shared Drive -->
          <div v-if="isAdminView || profile.shared_drive_url" class="sp-shared-drive-row">
            <div v-if="isAdminView" class="sp-shared-drive-edit">
              <fa-icon :icon="['fas', 'folder-open']" class="sp-drive-icon" />
              <span class="sp-drive-label">Shared Drive URL:</span>
              <input
                v-model="sharedDriveInput"
                class="sp-input sp-drive-input"
                placeholder="https://drive.google.com/..."
                @blur="saveSharedDrive"
              />
            </div>
            <a
              v-else-if="profile.shared_drive_url"
              :href="profile.shared_drive_url"
              target="_blank"
              rel="noopener noreferrer"
              class="sp-drive-btn"
            >
              <fa-icon :icon="['fas', 'folder-open']" /> Shared Drive
            </a>
          </div>

          </div><!-- /v-show collapse -->
          </transition>
        </div>

        <!-- ── REFERENCES SECTION ───────────────────────────── -->
        <div class="sp-card" :class="{ 'sp-card--collapsed': refsCollapsed }">
          <div class="sp-card-header sp-card-header--toggle" @click="refsCollapsed = !refsCollapsed">
            <fa-icon :icon="['fas', 'link']" />
            <span class="sp-card-title">References</span>
            <fa-icon :icon="['fas', refsCollapsed ? 'chevron-down' : 'chevron-up']" class="sp-card-chevron" />
          </div>
          <transition :css="false" @before-enter="collapseBeforeEnter" @enter="collapseEnter" @leave="collapseLeave">
          <div v-show="!refsCollapsed">
            <div v-if="refsLoading" class="sp-inner-loading"><fa-icon :icon="['fas', 'spinner']" spin /></div>
            <div v-else-if="references.length === 0" class="sp-empty-state">No references added yet.</div>
            <div v-else class="sp-ref-list">
              <div v-for="ref in references" :key="ref.id" class="sp-ref-card">
                <div class="sp-ref-main">
                  <a :href="ref.path" target="_blank" rel="noopener noreferrer" class="sp-ref-label">
                    <fa-icon :icon="['fas', 'external-link-alt']" /> {{ ref.label }}
                  </a>
                  <p v-if="ref.note" class="sp-ref-note">{{ ref.note }}</p>
                </div>
                <div v-if="isAdminView" class="sp-ref-actions">
                  <button class="sp-icon-btn" @click="openRefForm(ref)"><fa-icon :icon="['fas', 'pen']" /></button>
                  <button class="sp-icon-btn sp-icon-btn--danger" @click="deleteRef(ref)"><fa-icon :icon="['fas', 'trash']" /></button>
                </div>
              </div>
            </div>
            <div class="sp-add-ref-wrapper">
              <button v-if="isAdminView" class="sp-add-ref-btn" @click="openRefForm()">
                <fa-icon :icon="['fas', 'plus']" /> Add
              </button>
            </div>
          </div>
          </transition>
        </div>

        <!-- ── TASKS SECTION ────────────────────────────────── -->
        <div class="sp-card">
          <div class="sp-card-header">
            <fa-icon :icon="['fas', 'tasks']" />
            <span>{{ isAdminView ? 'Assigned Tasks' : 'My Tasks' }}</span>
          </div>

          <!-- Task tabs -->
          <div class="sp-task-tabs">
            <button :class="['sp-task-tab', { 'sp-task-tab--active': taskTab === 'active' }]" @click="taskTab = 'active'">
              Active <span class="sp-tab-count">{{ activeTasks.length }}</span>
            </button>
            <button :class="['sp-task-tab', { 'sp-task-tab--active': taskTab === 'closed' }]" @click="taskTab = 'closed'">
              Closed <span class="sp-tab-count">{{ closedTasks.length }}</span>
            </button>
            <button v-if="isAdminView" class="sp-tab-add-btn" @click="openTaskForm">
              <fa-icon :icon="['fas', 'plus']" /> Add Task
            </button>
          </div>

          <div v-if="tasksLoading" class="sp-inner-loading"><fa-icon :icon="['fas', 'spinner']" spin /></div>

          <div v-else-if="displayedTasks.length === 0" class="sp-empty-state">
            No {{ taskTab }} tasks.
          </div>

          <div v-else class="sp-task-list">
            <div
              v-for="task in displayedTasks"
              :key="task.id"
              class="sp-task-card"
              :class="{ 'sp-task-card--expanded': expandedTaskId === task.id }"
            >
              <!-- Task header (always visible) -->
              <div class="sp-task-card-head" @click="toggleTask(task.id)">
                <div class="sp-task-card-left">
                  <span class="sp-task-client">{{ task.client_name || '—' }}</span>
                  <span v-if="task.ya" class="sp-task-ya">YA {{ task.ya }}</span>
                  <span v-if="task.service_ids && task.service_ids.length" class="sp-task-services">
                    <span v-for="sid in task.service_ids" :key="sid" class="sp-svc-tag">{{ serviceLabel(sid) }}</span>
                  </span>
                </div>
                <div class="sp-task-card-right">
                  <span :class="['sp-status-badge', 'sp-status-badge--' + task.main_status]">{{ statusLabel(task.main_status) }}</span>
                  <fa-icon :icon="['fas', expandedTaskId === task.id ? 'chevron-up' : 'chevron-down']" class="sp-task-toggle-icon" />
                </div>
              </div>

              <!-- Task detail (expanded) -->
              <transition :css="false" @before-enter="collapseBeforeEnter" @enter="collapseEnter" @leave="collapseLeave">
              <div v-if="expandedTaskId === task.id" class="sp-task-detail">

                <!-- Financial info -->
                <div class="sp-task-info-grid">
                  <div class="sp-task-info-item" v-if="task.net_fee">
                    <span class="sp-label">Net Fee</span>
                    <span class="sp-value">RM {{ formatAmount(task.net_fee) }}</span>
                  </div>
                  <div class="sp-task-info-item" v-if="task.sharing_percentage">
                    <span class="sp-label">Sharing</span>
                    <span class="sp-value">{{ task.sharing_percentage }}% (RM {{ formatAmount(task.sharing_amount) }})</span>
                  </div>
                </div>

                <!-- Payments -->
                <div v-if="task.payments && task.payments.length" class="sp-section-sub">
                  <div class="sp-section-sub-title">Payment Schedule</div>
                  <div class="sp-pay-grid">
                    <div v-for="pay in task.payments" :key="pay.id" class="sp-pay-row">
                      <span class="sp-pay-group">Payment {{ pay.payment_group }}</span>
                      <span>{{ pay.percentage }}%</span>
                      <span>RM {{ formatAmount(pay.amount) }}</span>
                      <span>{{ pay.payment_date || '—' }}</span>
                    </div>
                  </div>
                </div>

                <!-- Draft steps -->
                <div class="sp-section-sub">
                  <div class="sp-section-sub-title">Progress</div>

                  <draggable
                    v-if="isAdminView"
                    v-model="task.draft_steps"
                    handle=".sp-drag-handle"
                    @end="saveDraftOrder(task)"
                    class="sp-draft-list"
                  >
                    <div v-for="step in task.draft_steps" :key="step.id" class="sp-draft-step">
                      <span class="sp-drag-handle"><fa-icon :icon="['fas', 'grip-vertical']" /></span>
                      <div class="sp-draft-info">
                        <span class="sp-draft-label">{{ step.label }}</span>
                        <div class="sp-draft-controls">
                          <div :class="['sp-draft-status-wrap', 'sp-draft-status-wrap--' + step.status, { 'sp-draft-status-wrap--loading': updatingStepId === step.id }]">
                            <select-input
                              :value="step.status"
                              :options="[
                                { value: 'pending', label: 'Pending' },
                                { value: 'in_progress', label: 'In Progress' },
                                { value: 'completed', label: 'Completed' }
                              ]"
                              :compact="true"
                              @input="updateDraftStatus(task, step, $event)"
                            />
                            <fa-icon v-if="updatingStepId === step.id" :icon="['fas', 'spinner']" spin class="sp-draft-status-spinner" />
                          </div>
                          <div class="sp-draft-date-picker">
                            <date-input
                              :value="isoToDmy(step.step_date)"
                              @input="val => updateDraftDate(task, step, dmyToIso(val))"
                            />
                          </div>
                          <button
                            v-if="!step.is_default"
                            class="sp-icon-btn sp-icon-btn--danger sp-draft-del"
                            @click="deleteDraftTarget = { task, step }"
                          ><fa-icon :icon="['fas', 'times']" /></button>
                        </div>
                      </div>
                    </div>
                  </draggable>
                  <button v-if="isAdminView" class="sp-add-draft-btn" @click="openAddDraft(task)">
                    <fa-icon :icon="['fas', 'plus']" /> Add Step
                  </button>

                  <!-- Staff view: read-only steps with date input allowed once -->
                  <div v-else class="sp-draft-list">
                    <div v-for="step in task.draft_steps" :key="step.id" class="sp-draft-step">
                      <span :class="['sp-draft-status-dot', 'sp-draft-status-dot--' + step.status]"></span>
                      <div class="sp-draft-info">
                        <span class="sp-draft-label">{{ step.label }}</span>
                        <span :class="['sp-draft-status-tag', 'sp-draft-status-tag--' + step.status]">{{ draftStatusLabel(step.status) }}</span>
                        <div class="sp-draft-date-wrap">
                          <span v-if="step.step_date" class="sp-draft-date-val">{{ isoToDmy(step.step_date) }}</span>
                          <div v-else-if="!step.date_locked" class="sp-draft-date-picker">
                            <date-input
                              value=""
                              @input="val => staffUpdateDraftDate(task, step, dmyToIso(val))"
                            />
                          </div>
                          <span v-else class="sp-draft-date-val sp-draft-date-val--empty">—</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Task actions (superadmin only) -->
                <div v-if="isAdminView" class="sp-task-actions-row">
                  <div class="sp-task-status-group sp-select-input">
                    <select-input
                      :value="task.main_status"
                      label="Status"
                      :options="taskStatusOptions"
                      @input="updateTaskStatus(task, $event)"
                    />
                  </div>
                  <div class="sp-task-reassign-group sp-select-input">
                    <select-input
                      value=""
                      label="Reassign To"
                      :options="reassignOptions"
                      placeholder="— select staff —"
                      :disabled="reassigningTaskId === task.id"
                      @input="reassignTask(task, $event)"
                    />
                    <span v-if="reassigningTaskId === task.id" class="sp-reassign-loading">
                      <fa-icon :icon="['fas', 'spinner']" spin /> Reassigning…
                    </span>
                  </div>
                  <div class="sp-task-btn-group">
                    <button class="sp-btn sp-btn-primary sp-btn-md" @click="openEditTask(task)">
                      <fa-icon :icon="['fas', 'pen']" /> Edit Task
                    </button>
                    <button class="sp-btn sp-btn-danger sp-btn-md" @click="confirmDeleteTask(task)">
                      <fa-icon :icon="['fas', 'trash']" /> Delete Task
                    </button>
                  </div>
                </div>

              </div>
              </transition>
            </div>
          </div>
          <div class="sp-add-task-wrapper">
            <button v-if="isAdminView && taskTab === 'active'" class="sp-add-task-btn" @click="openTaskForm">
              <fa-icon :icon="['fas', 'plus']" /> Add Task
            </button>
          </div>
        </div>

        <!-- ── LEAVE SECTION (staff view only) ────────────── -->
        <div v-if="!isAdminView" class="sp-card" :class="{ 'sp-card--collapsed': leaveCollapsed }">
          <div class="sp-card-header sp-card-header--toggle" @click="leaveCollapsed = !leaveCollapsed">
            <fa-icon :icon="['fas', 'calendar-minus']" />
            <span class="sp-card-title">My Leave</span>
            <fa-icon :icon="['fas', leaveCollapsed ? 'chevron-down' : 'chevron-up']" class="sp-card-chevron" />
          </div>

          <transition :css="false" @before-enter="collapseBeforeEnter" @enter="collapseEnter" @leave="collapseLeave">
          <div v-show="!leaveCollapsed">
          <!-- Submit Leave Form -->
          <div class="sp-leave-form-wrap">
            <p class="sp-leave-form-hint">Add a leave period — no approval needed. Superadmin will be notified automatically.</p>
            <form class="sp-leave-form" @submit.prevent="submitLeave" novalidate>
              <div class="sp-leave-date-row">
                <div class="sp-field">
                  <label class="sp-field-label">From <span class="sp-req">*</span></label>
                  <div class="sp-draft-date-picker" :class="{ 'sp-leave-date--err': leaveErrors.start_date }">
                    <date-input
                      :value="isoToDmy(leaveForm.start_date)"
                      @input="val => { leaveForm.start_date = dmyToIso(val); if (leaveForm.end_date && leaveForm.end_date < leaveForm.start_date) leaveForm.end_date = '' }"
                    />
                  </div>
                  <span v-if="leaveErrors.start_date" class="sp-err">{{ leaveErrors.start_date }}</span>
                </div>
                <div class="sp-leave-date-sep"><fa-icon :icon="['fas', 'arrow-right']" /></div>
                <div class="sp-field">
                  <label class="sp-field-label">To <span class="sp-req">*</span></label>
                  <div class="sp-draft-date-picker" :class="{ 'sp-leave-date--err': leaveErrors.end_date }">
                    <date-input
                      :value="isoToDmy(leaveForm.end_date)"
                      :min-date="isoToDmy(leaveForm.start_date)"
                      @input="val => { leaveForm.end_date = dmyToIso(val) }"
                    />
                  </div>
                  <span v-if="leaveErrors.end_date" class="sp-err">{{ leaveErrors.end_date }}</span>
                </div>
              </div>
              <div class="sp-field">
                <label class="sp-field-label">Note (optional)</label>
                <textarea
                  v-model="leaveForm.note"
                  class="sp-textarea"
                  rows="2"
                  placeholder="e.g. Medical leave, family trip…"
                ></textarea>
              </div>
              <div class="sp-leave-form-actions">
                <button type="submit" class="sp-btn sp-btn-primary" :disabled="leaveSubmitting">
                  <fa-icon v-if="leaveSubmitting" :icon="['fas', 'spinner']" spin />
                  {{ leaveSubmitting ? 'Submitting…' : 'Submit Leave' }}
                </button>
              </div>
            </form>
          </div>

          <!-- Leave Listing -->
          <div class="sp-leave-tabs">
            <button
              :class="['sp-task-tab', { 'sp-task-tab--active': leaveTab === 'upcoming' }]"
              @click="leaveTab = 'upcoming'"
            >
              Active / Upcoming <span class="sp-tab-count">{{ upcomingLeaves.length }}</span>
            </button>
            <button
              :class="['sp-task-tab', { 'sp-task-tab--active': leaveTab === 'past' }]"
              @click="leaveTab = 'past'"
            >
              Past <span class="sp-tab-count">{{ pastLeaves.length }}</span>
            </button>
          </div>

          <div v-if="leavesLoading" class="sp-inner-loading"><fa-icon :icon="['fas', 'spinner']" spin /></div>

          <div v-else-if="displayedLeaves.length === 0" class="sp-empty-state">
            No {{ leaveTab === 'upcoming' ? 'active or upcoming' : 'past' }} leaves.
          </div>

          <div v-else class="sp-leave-list">
            <div v-for="leave in displayedLeaves" :key="leave.id" class="sp-leave-row">
              <div class="sp-leave-row-left">
                <span :class="['sp-leave-status-dot', 'sp-leave-status-dot--' + leaveStatusLabel(leave).toLowerCase().replace(' ', '-')]"></span>
                <div class="sp-leave-row-info">
                  <span class="sp-leave-row-dates">
                    {{ formatDate(leave.start_date) }} – {{ formatDate(leave.end_date) }}
                  </span>
                  <span v-if="leave.note" class="sp-leave-row-note">{{ leave.note }}</span>
                </div>
              </div>
              <div class="sp-leave-row-right">
                <span :class="['sp-leave-chip', 'sp-leave-chip--' + leaveStatusLabel(leave).toLowerCase().replace(' ', '-')]">
                  {{ leaveStatusLabel(leave) }}
                </span>
                <button
                  v-if="leaveTab === 'upcoming'"
                  class="sp-icon-btn sp-icon-btn--danger"
                  @click="deleteLeave(leave)"
                  title="Remove leave"
                >
                  <fa-icon :icon="['fas', 'trash']" />
                </button>
              </div>
            </div>
          </div>
          </div><!-- /v-show collapse -->
          </transition>
        </div>

      </template>

      <div v-else class="sp-loading">Unable to load profile. Please try again.</div>
    </main>

    <!-- ── MODALS ─────────────────────────────────────────── -->

    <!-- Logout -->
    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="showLogoutModal = false"
      @confirm="doLogout"
    >Are you sure you want to logout?</confirm-modal>

    <!-- Delete task -->
    <confirm-modal
      :show="!!deleteTaskTarget"
      title="Delete Task"
      confirm-label="Delete"
      :icon="['fas', 'trash']"
      @cancel="deleteTaskTarget = null"
      @confirm="executeDeleteTask"
    >Are you sure you want to delete this task? This cannot be undone.</confirm-modal>

    <confirm-modal
      :show="!!deleteDraftTarget"
      title="Delete Step"
      confirm-label="Delete"
      :icon="['fas', 'trash']"
      @cancel="deleteDraftTarget = null"
      @confirm="executeDeleteDraftStep"
    >Are you sure you want to delete this step?</confirm-modal>

    <!-- Reference form modal -->
    <div v-if="showRefForm" class="sp-modal-overlay" @click.self="closeRefForm">
      <div class="sp-modal">
        <div class="sp-modal-head">
          <h3>{{ editingRef ? 'Edit Reference' : 'Add Reference' }}</h3>
          <button class="sp-modal-close" @click="closeRefForm"><fa-icon :icon="['fas', 'times']" /></button>
        </div>
        <form class="sp-modal-body" @submit.prevent="submitRef" novalidate>
          <div class="sp-field">
            <label class="sp-field-label">Label <span class="sp-req">*</span></label>
            <input v-model="refForm.label" class="sp-input" :class="{ 'sp-input--err': refErrors.label }" placeholder="e.g. Company Drive" />
            <span v-if="refErrors.label" class="sp-err">{{ refErrors.label }}</span>
          </div>
          <div class="sp-field">
            <label class="sp-field-label">Link / Path <span class="sp-req">*</span></label>
            <input v-model="refForm.path" class="sp-input" :class="{ 'sp-input--err': refErrors.path }" placeholder="https://..." />
            <span v-if="refErrors.path" class="sp-err">{{ refErrors.path }}</span>
          </div>
          <div class="sp-field">
            <label class="sp-field-label">Note</label>
            <textarea v-model="refForm.note" class="sp-textarea" rows="3" placeholder="Optional note…"></textarea>
          </div>
          <div class="sp-modal-actions">
            <button type="submit" class="sp-btn sp-btn-primary" :disabled="refSaving">
              <fa-icon v-if="refSaving" :icon="['fas', 'spinner']" spin />
              {{ refSaving ? 'Saving…' : (editingRef ? 'Save Changes' : 'Add Reference') }}
            </button>
            <button type="button" class="sp-btn sp-btn-ghost" @click="closeRefForm">Cancel</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Add Draft Step modal -->
    <div v-if="showAddDraftModal" class="sp-modal-overlay" @click.self="showAddDraftModal = false">
      <div class="sp-modal sp-modal--sm">
        <div class="sp-modal-head">
          <h3>Add Draft Step</h3>
          <button class="sp-modal-close" @click="showAddDraftModal = false"><fa-icon :icon="['fas', 'times']" /></button>
        </div>
        <div class="sp-modal-body">
          <div class="sp-field">
            <label class="sp-field-label">Step Label <span class="sp-req">*</span></label>
            <input v-model="newDraftLabel" class="sp-input" placeholder="e.g. Final Review" @keyup.enter="submitAddDraft" />
          </div>
          <div class="sp-modal-actions">
            <button class="sp-btn sp-btn-primary" @click="submitAddDraft" :disabled="!newDraftLabel.trim() || draftSaving">
              <fa-icon v-if="draftSaving" :icon="['fas', 'spinner']" spin /> Add Step
            </button>
            <button class="sp-btn sp-btn-ghost" @click="showAddDraftModal = false">Cancel</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Task side sheet -->
    <transition name="sp-sheet-anim">
    <div v-if="showTaskForm" class="sp-sheet-overlay" @mousedown.self.prevent>
      <div class="sp-sheet">
        <div class="sp-modal-head">
          <h3>{{ editingTask ? 'Edit Task' : 'Add Task' }} for {{ profile ? profile.full_name : '' }}</h3>
          <button class="sp-modal-close" @click="closeTaskForm"><fa-icon :icon="['fas', 'times']" /></button>
        </div>
        <form id="task-form" class="sp-modal-body sp-sheet-body" @submit.prevent="submitTask" @keydown.enter="onTaskFormEnter" novalidate>

          <!-- Client search -->
          <div class="sp-field" ref="clientField">
            <label class="sp-field-label">Client <span class="sp-req">*</span></label>
            <div class="sp-search-wrap">
              <fa-icon :icon="['fas', 'search']" class="sp-search-icon" />
              <input
                ref="clientSearchInput"
                v-model="clientSearch"
                class="sp-input sp-search-input"
                :class="{ 'sp-input--err': taskErrors.client_id }"
                placeholder="Search client name…"
                autocomplete="off"
                @input="onClientSearchInput"
                @focus="onClientSearchInput"
                @blur="clientDropOpen = false"
                @keydown.escape="clientDropOpen = false"
              />
              <span v-if="taskForm.client_id" class="sp-search-clear" @click="clearClient">
                <fa-icon :icon="['fas', 'times']" />
              </span>
            </div>
            <div
              v-if="clientDropOpen && filteredClients.length"
              class="sp-dropdown sp-dropdown--fixed"
              :style="{ top: clientDropPos.top + 'px', left: clientDropPos.left + 'px', width: clientDropPos.width + 'px' }"
            >
              <div
                v-for="c in filteredClients"
                :key="c.id"
                class="sp-dropdown-item"
                @mousedown.prevent="selectClient(c)"
              >
                <span>{{ c.client }}</span>
                <span v-if="c.initial" class="sp-dd-initial">{{ c.initial }}</span>
              </div>
            </div>
            <div
              v-if="clientDropOpen && clientSearch.length >= 1 && !filteredClients.length"
              class="sp-dropdown sp-dropdown--fixed sp-dropdown--empty"
              :style="{ top: clientDropPos.top + 'px', left: clientDropPos.left + 'px', width: clientDropPos.width + 'px' }"
            >
              No clients found
            </div>
            <span v-if="taskErrors.client_id" class="sp-err">{{ taskErrors.client_id }}</span>
          </div>

          <!-- Services multi-select -->
          <div class="sp-field">
            <label class="sp-field-label">Service</label>
            <div class="sp-checkbox-group">
              <label v-for="svc in services" :key="svc.id" class="sp-checkbox-label">
                <input type="checkbox" :value="svc.slug" v-model="taskForm.service_ids" />
                {{ svc.name }}
              </label>
            </div>
          </div>

          <!-- YA -->
          <div class="sp-select-input">
            <select-input
              v-model="taskForm.ya"
              label="YA (Year of Assessment)"
              :options="yaOptions"
              placeholder="— select year —"
            />
          </div>

          <div class="sp-form-row">
            <!-- Net Fee -->
            <div class="sp-field">
              <label class="sp-field-label">Net Fee (RM)</label>
              <input
                v-model="taskForm.net_fee_display"
                class="sp-input"
                placeholder="0"
                @input="onNetFeeInput"
                @blur="formatNetFee"
              />
            </div>

            <!-- Sharing % -->
            <div class="sp-field">
              <label class="sp-field-label">Sharing (%)</label>
              <div class="sp-pct-wrap">
                <input
                  v-model.number="taskForm.sharing_percentage"
                  class="sp-input"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  @input="onSharingPctInput"
                />
                <span class="sp-pct-preview" v-if="sharingAmount !== null">= RM {{ formatAmount(sharingAmount) }}</span>
              </div>
            </div>
          </div>

          <!-- Payments -->
          <div class="sp-section-sub">
            <div class="sp-section-sub-title">Payment Schedule</div>
            <div
              v-for="(pay, i) in taskForm.payments"
              :key="i"
              class="sp-pay-form-row"
              :class="{ 'sp-pay-form-row--disabled': i > 0 && !taskForm.payments[i-1].percentage }"
            >
              <span class="sp-pay-group-label">Payment {{ i + 1 }}</span>
              <div class="sp-pay-form-fields">
                <div class="sp-pay-field">
                  <label class="sp-field-label-sm">% of Sharing</label>
                  <input
                    v-model.number="pay.percentage"
                    class="sp-input sp-input-sm"
                    type="number" min="0" max="100"
                    placeholder="0"
                    :disabled="i > 0 && !taskForm.payments[i-1].percentage"
                    @input="onPaymentPctInput(i)"
                  />
                </div>
                <div class="sp-pay-field">
                  <label class="sp-field-label-sm">Amount</label>
                  <input
                    :value="paymentAmount(i)"
                    class="sp-input sp-input-sm"
                    readonly
                    placeholder="0.00"
                  />
                </div>
                <div class="sp-pay-field date-field" :class="{ 'sp-pay-date--disabled': i > 0 && !taskForm.payments[i-1].percentage }">
                  <date-input
                    :value="isoToDmy(pay.payment_date)"
                    label="Payment Date"
                    @input="val => pay.payment_date = dmyToIso(val)"
                  />
                </div>
              </div>
            </div>
            <span v-if="taskErrors.payments" class="sp-err">{{ taskErrors.payments }}</span>
            <div class="sp-pay-total" v-if="filledPaymentTotal > 0">
              <span :class="paymentTotalOk ? 'sp-pay-total--ok' : 'sp-pay-total--err'">
                Total: {{ filledPaymentTotal.toFixed(2) }}%
                <span v-if="paymentTotalOk"> ✓</span>
                <span v-else> (must be 100%)</span>
              </span>
            </div>
          </div>

          <div v-if="taskErrors.general" class="sp-err sp-err-block">{{ taskErrors.general }}</div>

        </form>
        <div class="sp-sheet-footer">
          <button type="submit" form="task-form" class="sp-btn sp-btn-primary" :disabled="taskSaving">
            <fa-icon v-if="taskSaving" :icon="['fas', 'spinner']" spin /> {{ taskSaving ? 'Saving…' : (editingTask ? 'Save Changes' : 'Create Task') }}
          </button>
          <button type="button" class="sp-btn sp-btn-ghost" @click="closeTaskForm">Cancel</button>
        </div>
      </div>
    </div>
    </transition>

    <!-- Alert -->
    <alert-modal :show="showAlert" :message="alertMessage" :status="alertStatus" @close="showAlert = false" />

  </div>
</template>

<script>
import { staffFetch, apiFetch } from '@/services/api'
import { withLoader } from '@/services/loader'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import AlertModal from '@/components/shared/AlertModal.vue'
import draggable from 'vuedraggable'
import EmailInput from '@/components/form/EmailInput.vue'
import PhoneInput from '@/components/form/PhoneInput.vue'
import SelectInput from '@/components/form/SelectInput.vue'
import TextInput from '@/components/form/TextInput.vue'
import DateInput from '@/components/form/DateInput.vue'

const MALAYSIAN_BANKS = [
  'Maybank', 'CIMB Bank', 'Public Bank Berhad', 'RHB Bank', 'Hong Leong Bank',
  'AmBank', 'UOB Malaysia', 'OCBC Bank Malaysia', 'HSBC Bank Malaysia',
  'Standard Chartered Bank Malaysia', 'Alliance Bank Malaysia', 'Affin Bank',
  'Bank Islam Malaysia', 'Bank Muamalat Malaysia', 'Bank Rakyat',
  'MBSB Bank', 'Agrobank', 'BSN (Bank Simpanan Nasional)', 'Bank of China (Malaysia)',
  'EXIM Bank'
]

export default {
  name: 'staff-panel',
  components: { ConfirmModal, AlertModal, draggable, EmailInput, PhoneInput, SelectInput, TextInput, DateInput },

  props: {
    // When loaded from /admin-panel/staff/:id the route provides the id param.
    // viewMode is inferred from whether we have an adminToken or staffToken.
  },

  data () {
    return {
      // Context
      isAdminView: false,
      staffId: null,

      // Profile
      profile: null,
      loading: true,
      profileCollapsed: true,
      refsCollapsed: true,
      leaveCollapsed: true,
      editingProfile: false,
      profileSaving: false,
      profileForm: {},
      profileErrors: {},

      // Shared drive
      sharedDriveInput: '',

      // References
      references: [],
      refsLoading: false,
      showRefForm: false,
      editingRef: null,
      refForm: { label: '', path: '', note: '' },
      refErrors: {},
      refSaving: false,

      // Tasks
      tasks: [],
      tasksLoading: false,
      taskTab: 'active',
      expandedTaskId: null,
      showTaskForm: false,
      taskSaving: false,
      taskErrors: {},
      deleteTaskTarget: null,
      deleteDraftTarget: null,
      updatingStepId: null,
      editingTask: null,
      reassigningTaskId: null,

      // Task form data
      clientSearch: '',
      clientDropOpen: false,
      clientDropPos: { top: 0, left: 0, width: 0 },
      allClients: [],
      services: [],
      allStaff: [],
      taskForm: {
        client_id: null,
        service_ids: [],
        ya: '',
        net_fee_display: '',
        net_fee: null,
        sharing_percentage: null,
        payments: [
          { percentage: null, payment_date: '' },
          { percentage: null, payment_date: '' },
          { percentage: null, payment_date: '' },
          { percentage: null, payment_date: '' }
        ]
      },

      // Draft step
      showAddDraftModal: false,
      newDraftLabel: '',
      draftSaving: false,
      addDraftTargetTask: null,

      // Leaves (staff-only)
      leaves: [],
      leavesLoading: false,
      leaveTab: 'upcoming',
      serverDate: '',
      leaveForm: { start_date: '', end_date: '', note: '' },
      leaveSubmitting: false,
      leaveErrors: {},

      // Misc
      showLogoutModal: false,
      showAlert: false,
      alertMessage: '',
      alertStatus: 'success'
    }
  },

  computed: {
    bankOptions () {
      return MALAYSIAN_BANKS.map(b => ({ value: b, label: b }))
    },
    initials () {
      if (!this.profile) return '?'
      return this.profile.full_name
        .split(' ')
        .slice(0, 2)
        .map(w => w.charAt(0).toUpperCase())
        .join('')
    },
    activeTasks () {
      return this.tasks.filter(t => t.main_status !== 'closed')
    },
    closedTasks () {
      return this.tasks.filter(t => t.main_status === 'closed')
    },
    displayedTasks () {
      return this.taskTab === 'active' ? this.activeTasks : this.closedTasks
    },
    filteredClients () {
      const q = this.clientSearch.toLowerCase().trim()
      if (!q) return this.allClients.slice(0, 20)
      return this.allClients.filter(c => c.client.toLowerCase().includes(q)).slice(0, 12)
    },
    yaYears () {
      const current = new Date().getFullYear()
      const years = []
      for (let y = current + 10; y >= 2010; y--) years.push(y)
      return years
    },
    yaOptions () {
      return this.yaYears.map(y => ({ value: y, label: String(y) }))
    },
    taskStatusOptions () {
      return [
        { value: 'pending', label: 'Pending' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'closed', label: 'Closed' }
      ]
    },
    reassignOptions () {
      return this.allStaff
        .filter(s => !this.profile || s.id !== this.profile.id)
        .map(s => ({ value: s.id, label: s.full_name }))
    },
    sharingAmount () {
      const fee = this.taskForm.net_fee
      const pct = this.taskForm.sharing_percentage
      if (fee === null || pct === null || fee === '' || pct === '') return null
      return Math.round(parseFloat(fee) * parseFloat(pct)) / 100
    },
    filledPaymentTotal () {
      return this.taskForm.payments
        .filter(p => p.percentage !== null && p.percentage !== '')
        .reduce((s, p) => s + parseFloat(p.percentage || 0), 0)
    },
    paymentTotalOk () {
      const filled = this.taskForm.payments.filter(p => p.percentage !== null && p.percentage !== '')
      if (!filled.length) return true
      return Math.abs(this.filledPaymentTotal - 100) < 0.01
    },
    upcomingLeaves () {
      if (!this.serverDate) return []
      return this.leaves.filter(l => l.end_date >= this.serverDate)
    },
    pastLeaves () {
      if (!this.serverDate) return []
      return this.leaves.filter(l => l.end_date < this.serverDate)
    },
    displayedLeaves () {
      return this.leaveTab === 'upcoming' ? this.upcomingLeaves : this.pastLeaves
    },
    bannerLeaves () {
      if (!this.serverDate) return []
      return this.leaves.filter(l => l.end_date >= this.serverDate)
    }
  },

  created () {
    const adminToken = localStorage.getItem('adminToken')
    const staffToken = localStorage.getItem('staffToken')

    if (adminToken && this.$route.params.id) {
      this.isAdminView = true
      this.staffId = parseInt(this.$route.params.id)
    } else if (staffToken) {
      this.isAdminView = false
      this.staffId = null
    }

    this.fetchProfile()
  },

  mounted () {
    window.addEventListener('scroll', this.closeClientDrop, true)
    window.addEventListener('resize', this.closeClientDrop)
  },

  beforeDestroy () {
    window.removeEventListener('scroll', this.closeClientDrop, true)
    window.removeEventListener('resize', this.closeClientDrop)
    document.body.style.overflow = ''
  },

  watch: {
    taskTab (val) {
      const list = val === 'active' ? this.activeTasks : this.closedTasks
      this.expandedTaskId = list.length ? list[0].id : null
    },
    showTaskForm (val) {
      document.body.style.overflow = val ? 'hidden' : ''
    }
  },

  methods: {
    // ── Collapse animation ───────────────────────────────
    collapseBeforeEnter (el) {
      el.style.height = '0'
      el.style.overflow = 'hidden'
    },
    collapseEnter (el, done) {
      const h = el.scrollHeight
      el.style.transition = 'height 0.32s cubic-bezier(0.4, 0, 0.2, 1)'
      el.style.height = h + 'px'
      el.addEventListener('transitionend', () => {
        el.style.height = ''
        el.style.overflow = ''
        el.style.transition = ''
        done()
      }, { once: true })
    },
    collapseLeave (el, done) {
      el.style.height = el.scrollHeight + 'px'
      el.style.overflow = 'hidden'
      el.getBoundingClientRect()
      el.style.transition = 'height 0.28s cubic-bezier(0.4, 0, 0.2, 1)'
      el.style.height = '0'
      el.addEventListener('transitionend', () => {
        el.style.transition = ''
        done()
      }, { once: true })
    },
    // ── Fetch ────────────────────────────────────────────
    async fetchProfile () {
      this.loading = true
      try {
        let data
        if (this.isAdminView) {
          data = await apiFetch(`/api/admin/staff/get-profile.php?id=${this.staffId}`)
        } else {
          data = await staffFetch('/api/admin/user-management/get-my-profile.php')
        }
        if (data && data.success) {
          this.profile = data.profile
          this.sharedDriveInput = data.profile.shared_drive_url || ''
          await Promise.all([this.fetchReferences(), this.fetchTasks()])
          if (this.isAdminView) {
            await Promise.all([this.fetchServices(), this.fetchClients(), this.fetchAllStaff(), this.fetchServerDate(), this.fetchLeaves()])
          } else {
            await Promise.all([this.fetchServices(), this.fetchServerDate(), this.fetchLeaves()])
          }
        } else if (!this.isAdminView) {
          this.forceStaffLogout()
        }
      } catch {
        if (!this.isAdminView) {
          this.forceStaffLogout()
        }
      } finally {
        this.loading = false
      }
    },

    async fetchReferences () {
      this.refsLoading = true
      const id = this.isAdminView ? this.staffId : (this.profile ? this.profile.id : null)
      if (!id) { this.refsLoading = false; return }
      try {
        const data = this.isAdminView
          ? await apiFetch(`/api/admin/staff/references/list.php?staff_id=${id}`)
          : await staffFetch(`/api/admin/staff/references/list.php?staff_id=${id}`)
        if (data && data.success) this.references = data.references
      } finally {
        this.refsLoading = false
      }
    },

    async fetchTasks () {
      this.tasksLoading = true
      const id = this.isAdminView ? this.staffId : (this.profile ? this.profile.id : null)
      if (!id) { this.tasksLoading = false; return }
      try {
        const data = this.isAdminView
          ? await apiFetch(`/api/admin/staff/tasks/list.php?staff_id=${id}`)
          : await staffFetch(`/api/admin/staff/tasks/list.php?staff_id=${id}`)
        if (data && data.success) {
          this.tasks = data.tasks
          const first = this.activeTasks[0]
          if (first) this.expandedTaskId = first.id
        }
      } finally {
        this.tasksLoading = false
      }
    },

    async fetchServices () {
      const data = this.isAdminView
        ? await apiFetch('/api/admin/shared/get-services.php')
        : await staffFetch('/api/admin/shared/get-services.php')
      if (data && data.success) this.services = data.services
    },

    async fetchClients () {
      const data = await apiFetch('/api/admin/shared/get-clients-list.php')
      if (data && data.success) this.allClients = data.clients
    },

    async fetchAllStaff () {
      const data = await apiFetch('/api/admin/user-management/list-staff.php')
      if (data && data.success) this.allStaff = data.staff
    },

    // ── Profile ──────────────────────────────────────────
    startEditProfile () {
      this.profileForm = {
        full_name: this.profile.full_name,
        username: this.profile.username,
        ic_number: this.profile.ic_number || '',
        email: this.profile.email || '',
        phone: (this.profile.phone_country || '') + (this.profile.phone_number || ''),
        bank_name: this.profile.bank_name || '',
        bank_account: this.profile.bank_account || '',
        password: '',
        status: this.profile.status
      }
      this.profileErrors = {}
      this.editingProfile = true
    },

    async submitProfile () {
      this.profileErrors = {}
      if (!this.profileForm.full_name) this.profileErrors.full_name = 'Full name is required'
      if (this.profileForm.password && this.profileForm.password.length < 6) this.profileErrors.password = 'Min 6 characters'
      if (Object.keys(this.profileErrors).length) return

      this.profileSaving = true
      try {
        const { phone, ...rest } = this.profileForm
        const payload = { ...rest, phone_number: phone, phone_country: '', id: this.profile.id }
        const url = '/api/admin/staff/update-profile.php'
        const opts = { method: 'PUT', body: JSON.stringify(payload) }
        const data = this.isAdminView
          ? await apiFetch(url, opts)
          : await staffFetch(url, opts)

        if (data && data.success) {
          this.profile = data.profile
          this.editingProfile = false
          this.showAlertMsg('Profile updated', 'success')
        } else {
          if (data && data.errors) this.profileErrors = data.errors
          else this.showAlertMsg(data?.message || 'Failed to update', 'error')
        }
      } finally {
        this.profileSaving = false
      }
    },

    async saveSharedDrive () {
      if (!this.isAdminView) return
      const data = await apiFetch('/api/admin/staff/update-shared-drive.php', {
        method: 'PUT',
        body: JSON.stringify({ id: this.staffId, shared_drive_url: this.sharedDriveInput })
      })
      if (data && data.success) {
        this.showAlertMsg('Shared Drive URL updated', 'success')
      } else {
        this.showAlertMsg((data && data.message) || 'Failed to update Shared Drive URL', 'error')
      }
    },

    // ── References ───────────────────────────────────────
    openRefForm (ref = null) {
      this.editingRef = ref
      this.refForm = ref
        ? { label: ref.label, path: ref.path, note: ref.note || '' }
        : { label: '', path: '', note: '' }
      this.refErrors = {}
      this.showRefForm = true
    },
    closeRefForm () { this.showRefForm = false; this.editingRef = null },

    async submitRef () {
      this.refErrors = {}
      if (!this.refForm.label.trim()) this.refErrors.label = 'Label is required'
      if (!this.refForm.path.trim()) this.refErrors.path = 'Link is required'
      if (Object.keys(this.refErrors).length) return

      this.refSaving = true
      try {
        const staffId = this.isAdminView ? this.staffId : this.profile.id
        let data
        if (this.editingRef) {
          data = await apiFetch('/api/admin/staff/references/update.php', {
            method: 'PUT',
            body: JSON.stringify({ id: this.editingRef.id, ...this.refForm })
          })
          if (data && data.success) {
            const idx = this.references.findIndex(r => r.id === this.editingRef.id)
            if (idx !== -1) this.$set(this.references, idx, data.reference)
          }
        } else {
          data = await apiFetch('/api/admin/staff/references/create.php', {
            method: 'POST',
            body: JSON.stringify({ staff_id: staffId, ...this.refForm })
          })
          if (data && data.success) this.references.push(data.reference)
        }
        if (data && data.success) {
          this.closeRefForm()
          this.showAlertMsg('Reference saved', 'success')
        } else {
          this.showAlertMsg(data?.message || 'Failed to save', 'error')
        }
      } finally {
        this.refSaving = false
      }
    },

    async deleteRef (ref) {
      const data = await apiFetch('/api/admin/staff/references/delete.php', {
        method: 'DELETE',
        body: JSON.stringify({ id: ref.id })
      })
      if (data && data.success) {
        this.references = this.references.filter(r => r.id !== ref.id)
        this.showAlertMsg('Reference deleted', 'success')
      }
    },

    // ── Tasks ────────────────────────────────────────────
    openTaskForm () {
      this.editingTask = null
      this.taskForm = {
        client_id: null,
        service_ids: [],
        ya: '',
        net_fee_display: '',
        net_fee: null,
        sharing_percentage: null,
        payments: Array.from({ length: 4 }, () => ({ percentage: null, payment_date: '' }))
      }
      this.clientSearch = ''
      this.clientDropOpen = false
      this.taskErrors = {}
      this.showTaskForm = true
    },
    closeTaskForm () { this.showTaskForm = false; this.editingTask = null },

    openEditTask (task) {
      this.editingTask = task
      this.clientSearch = task.client_name || ''
      const paySlots = Array.from({ length: 4 }, () => ({ percentage: null, payment_date: '' }))
      ;(task.payments || []).forEach(p => {
        const idx = (p.payment_group || 1) - 1
        if (idx >= 0 && idx < 4) {
          paySlots[idx] = {
            percentage: p.percentage !== null && p.percentage !== undefined ? parseFloat(p.percentage) : null,
            payment_date: p.payment_date || ''
          }
        }
      })
      const netFee = task.net_fee !== null && task.net_fee !== undefined ? parseFloat(task.net_fee) : null
      this.taskForm = {
        client_id: task.client_id,
        service_ids: task.service_ids ? [...task.service_ids] : [],
        ya: task.ya || '',
        net_fee_display: netFee !== null ? this.formatAmount(netFee) : '',
        net_fee: netFee,
        sharing_percentage: task.sharing_percentage !== null && task.sharing_percentage !== undefined
          ? parseFloat(task.sharing_percentage) : null,
        payments: paySlots
      }
      this.taskErrors = {}
      this.clientDropOpen = false
      this.showTaskForm = true
    },

    toggleTask (id) { this.expandedTaskId = this.expandedTaskId === id ? null : id },

    async submitTask () {
      this.taskErrors = {}
      if (!this.taskForm.client_id) this.taskErrors.client_id = 'Client is required'

      const filled = this.taskForm.payments.filter(p => p.percentage !== null && p.percentage !== '')
      if (filled.length && Math.abs(this.filledPaymentTotal - 100) > 0.01) {
        this.taskErrors.payments = 'Payment percentages must total 100%'
      }
      if (Object.keys(this.taskErrors).length) return

      this.taskSaving = true
      try {
        const payload = {
          client_id: this.taskForm.client_id,
          service_ids: this.taskForm.service_ids,
          ya: this.taskForm.ya || null,
          net_fee: this.taskForm.net_fee,
          sharing_percentage: this.taskForm.sharing_percentage,
          payments: this.taskForm.payments
        }

        if (this.editingTask) {
          const data = await apiFetch('/api/admin/staff/tasks/update.php', {
            method: 'PUT',
            body: JSON.stringify({ task_id: this.editingTask.id, ...payload })
          })
          if (data && data.success) {
            const idx = this.tasks.findIndex(t => t.id === this.editingTask.id)
            if (idx !== -1) this.$set(this.tasks, idx, data.task)
            this.closeTaskForm()
            this.showAlertMsg('Task updated', 'success')
          } else {
            if (data && data.errors) this.taskErrors = data.errors
            else this.showAlertMsg(data?.message || 'Failed to update task', 'error')
          }
        } else {
          const data = await apiFetch('/api/admin/staff/tasks/create.php', {
            method: 'POST',
            body: JSON.stringify({ staff_id: this.staffId, ...payload })
          })
          if (data && data.success) {
            this.closeTaskForm()
            await this.fetchTasks()
            this.showAlertMsg('Task created', 'success')
          } else {
            if (data && data.errors) this.taskErrors = data.errors
            else this.showAlertMsg(data?.message || 'Failed to create task', 'error')
          }
        }
      } finally {
        this.taskSaving = false
      }
    },

    confirmDeleteTask (task) { this.deleteTaskTarget = task },

    async executeDeleteTask () {
      if (!this.deleteTaskTarget) return
      const id = this.deleteTaskTarget.id
      this.deleteTaskTarget = null
      const data = await apiFetch('/api/admin/staff/tasks/delete.php', {
        method: 'DELETE',
        body: JSON.stringify({ task_id: id })
      })
      if (data && data.success) {
        this.tasks = this.tasks.filter(t => t.id !== id)
        this.expandedTaskId = null
        this.showAlertMsg('Task deleted', 'success')
      }
    },

    async updateTaskStatus (task, status) {
      await withLoader(async () => {
        const data = await apiFetch('/api/admin/staff/tasks/update-status.php', {
          method: 'PUT',
          body: JSON.stringify({ task_id: task.id, status })
        })
        if (data && data.success) {
          task.main_status = status
          this.showAlertMsg('Status updated', 'success')
        } else {
          this.showAlertMsg((data && data.message) || 'Failed to update status', 'error')
        }
      })
    },

    async reassignTask (task, newStaffId) {
      if (!newStaffId) return
      this.reassigningTaskId = task.id
      await withLoader(async () => {
        try {
          const data = await apiFetch('/api/admin/staff/tasks/reassign.php', {
            method: 'PUT',
            body: JSON.stringify({ task_id: task.id, new_staff_id: parseInt(newStaffId) })
          })
          if (data && data.success) {
            this.tasks = this.tasks.filter(t => t.id !== task.id)
            this.expandedTaskId = null
            this.showAlertMsg('Task reassigned', 'success')
          } else {
            this.showAlertMsg(data?.message || 'Failed to reassign task', 'error')
          }
        } catch {
          this.showAlertMsg('Failed to reassign task', 'error')
        }
      })
      this.reassigningTaskId = null
    },

    // ── Draft steps ──────────────────────────────────────
    openAddDraft (task) {
      this.addDraftTargetTask = task
      this.newDraftLabel = ''
      this.showAddDraftModal = true
    },

    async submitAddDraft () {
      if (!this.newDraftLabel.trim()) return
      this.draftSaving = true
      try {
        const data = await apiFetch('/api/admin/staff/tasks/draft/add.php', {
          method: 'POST',
          body: JSON.stringify({ task_id: this.addDraftTargetTask.id, label: this.newDraftLabel.trim() })
        })
        if (data && data.success) {
          this.addDraftTargetTask.draft_steps.push(data.step)
          this.showAddDraftModal = false
          this.newDraftLabel = ''
        }
      } finally {
        this.draftSaving = false
      }
    },

    async saveDraftOrder (task) {
      await apiFetch('/api/admin/staff/tasks/draft/reorder.php', {
        method: 'PUT',
        body: JSON.stringify({ task_id: task.id, ordered_ids: task.draft_steps.map(s => s.id) })
      })
    },

    async updateDraftStatus (task, step, status) {
      this.updatingStepId = step.id
      await withLoader(async () => {
        const data = await apiFetch('/api/admin/staff/tasks/draft/update.php', {
          method: 'PUT',
          body: JSON.stringify({ step_id: step.id, status })
        })
        if (data && data.success) {
          const idx = task.draft_steps.findIndex(s => s.id === step.id)
          if (idx !== -1) this.$set(task.draft_steps, idx, data.step)
          this.showAlertMsg('Step status updated', 'success')
        } else {
          this.showAlertMsg((data && data.message) || 'Failed to update step status', 'error')
        }
      })
      this.updatingStepId = null
    },

    async updateDraftDate (task, step, date) {
      const data = await apiFetch('/api/admin/staff/tasks/draft/update.php', {
        method: 'PUT',
        body: JSON.stringify({ step_id: step.id, step_date: date || null })
      })
      if (data && data.success) {
        const idx = task.draft_steps.findIndex(s => s.id === step.id)
        if (idx !== -1) this.$set(task.draft_steps, idx, data.step)
      }
    },

    async staffUpdateDraftDate (task, step, date) {
      const data = await staffFetch('/api/admin/staff/tasks/draft/update.php', {
        method: 'PUT',
        body: JSON.stringify({ step_id: step.id, step_date: date || null })
      })
      if (data && data.success) {
        const idx = task.draft_steps.findIndex(s => s.id === step.id)
        if (idx !== -1) this.$set(task.draft_steps, idx, data.step)
      } else if (data && data.message) {
        this.showAlertMsg(data.message, 'error')
      }
    },

    async executeDeleteDraftStep () {
      if (!this.deleteDraftTarget) return
      const { task, step } = this.deleteDraftTarget
      this.deleteDraftTarget = null
      const data = await apiFetch('/api/admin/staff/tasks/draft/delete.php', {
        method: 'DELETE',
        body: JSON.stringify({ step_id: step.id })
      })
      if (data && data.success) {
        task.draft_steps = task.draft_steps.filter(s => s.id !== step.id)
      }
    },

    // ── Task form Enter-key navigation ───────────────────
    onTaskFormEnter (e) {
      if (e.target.tagName === 'BUTTON') return
      e.preventDefault()
      if (this.clientDropOpen) this.clientDropOpen = false
      const form = e.currentTarget
      const focusable = [
        ...form.querySelectorAll(
          'input:not([type="checkbox"]):not([readonly]):not([disabled]), select:not([disabled])'
        )
      ]
      const idx = focusable.indexOf(e.target)
      if (idx >= 0 && idx < focusable.length - 1) {
        focusable[idx + 1].focus()
      }
    },

    // ── Client search ────────────────────────────────────
    selectClient (c) {
      this.taskForm.client_id = c.id
      this.clientSearch = c.client
      this.clientDropOpen = false
      this.taskErrors.client_id = ''
    },
    clearClient () {
      this.taskForm.client_id = null
      this.clientSearch = ''
      this.clientDropOpen = false
    },
    onClientSearchInput () {
      if (this.$refs.clientSearchInput) {
        const rect = this.$refs.clientSearchInput.getBoundingClientRect()
        this.clientDropPos = { top: rect.bottom + 4, left: rect.left, width: rect.width }
      }
      this.clientDropOpen = true
    },
    closeClientDrop () {
      this.clientDropOpen = false
    },

    // ── Net fee / sharing calc ────────────────────────────
    onNetFeeInput () {
      const raw = this.taskForm.net_fee_display.replace(/[^0-9.]/g, '')
      this.taskForm.net_fee = raw ? parseFloat(raw) : null
    },
    formatNetFee () {
      if (this.taskForm.net_fee !== null) {
        this.taskForm.net_fee_display = this.formatAmount(this.taskForm.net_fee)
      }
    },
    onSharingPctInput () {
      // Clamp to 0-100
      if (this.taskForm.sharing_percentage > 100) this.taskForm.sharing_percentage = 100
      if (this.taskForm.sharing_percentage < 0) this.taskForm.sharing_percentage = 0
    },
    onPaymentPctInput (i) {
      if (this.taskForm.payments[i].percentage > 100) this.taskForm.payments[i].percentage = 100
      if (this.taskForm.payments[i].percentage < 0) this.taskForm.payments[i].percentage = 0
    },
    paymentAmount (i) {
      const pct = this.taskForm.payments[i].percentage
      if (!pct || this.sharingAmount === null) return '0.00'
      return this.formatAmount(Math.round(this.sharingAmount * parseFloat(pct)) / 100)
    },

    // ── Helpers ──────────────────────────────────────────
    serviceLabel (slug) {
      const svc = this.services.find(s => s.slug === slug)
      return svc ? svc.name : slug
    },
    statusLabel (s) {
      return { pending: 'Pending', in_progress: 'In Progress', closed: 'Closed' }[s] || s
    },
    draftStatusLabel (s) {
      return { pending: 'Pending', in_progress: 'In Progress', completed: 'Completed' }[s] || s
    },
    isoToDmy (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-')
      return (y && m && d) ? `${d}/${m}/${y}` : iso
    },
    dmyToIso (dmy) {
      if (!dmy) return ''
      const [d, m, y] = dmy.split('/')
      return (d && m && y) ? `${y}-${m}-${d}` : dmy
    },
    formatDate (dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('en-MY', { day: '2-digit', month: 'long', year: 'numeric' })
    },
    formatAmount (val) {
      if (val === null || val === undefined || val === '') return '0.00'
      return parseFloat(val).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    goBack () {
      this.$router.push({ name: 'Admin-User-Management', params: { lang: this.$route.params.lang } })
    },
    showAlertMsg (msg, status) {
      this.alertMessage = msg
      this.alertStatus = status
      this.showAlert = true
    },

    // ── Leaves ───────────────────────────────────────────
    async fetchServerDate () {
      try {
        const data = this.isAdminView
          ? await apiFetch('/api/admin/staff/server-date.php')
          : await staffFetch('/api/admin/staff/server-date.php')
        if (data && data.success) this.serverDate = data.date
      } catch {}
    },

    async fetchLeaves () {
      this.leavesLoading = true
      try {
        const url = this.isAdminView
          ? `/api/admin/staff/leaves/list.php?id=${this.staffId}`
          : '/api/admin/staff/leaves/list.php'
        const data = this.isAdminView
          ? await apiFetch(url)
          : await staffFetch(url)
        if (data && data.success) this.leaves = data.leaves
      } finally {
        this.leavesLoading = false
      }
    },

    async submitLeave () {
      this.leaveErrors = {}
      if (!this.leaveForm.start_date) this.leaveErrors.start_date = 'Start date is required'
      if (!this.leaveForm.end_date) this.leaveErrors.end_date = 'End date is required'
      if (this.leaveForm.start_date && this.leaveForm.end_date && this.leaveForm.end_date < this.leaveForm.start_date) {
        this.leaveErrors.end_date = 'End date must not be before start date'
      }
      if (Object.keys(this.leaveErrors).length) return

      this.leaveSubmitting = true
      try {
        const data = await staffFetch('/api/admin/staff/leaves/create.php', {
          method: 'POST',
          body: JSON.stringify(this.leaveForm)
        })
        if (data && data.success) {
          this.leaves.unshift(data.leave)
          this.leaveForm = { start_date: '', end_date: '', note: '' }
          this.leaveErrors = {}
          this.showAlertMsg('Leave submitted. Superadmin has been notified.', 'success')
        } else {
          this.showAlertMsg(data?.message || 'Failed to submit leave', 'error')
        }
      } finally {
        this.leaveSubmitting = false
      }
    },

    async deleteLeave (leave) {
      const data = await staffFetch('/api/admin/staff/leaves/delete.php', {
        method: 'DELETE',
        body: JSON.stringify({ id: leave.id })
      })
      if (data && data.success) {
        this.leaves = this.leaves.filter(l => l.id !== leave.id)
        this.showAlertMsg('Leave removed', 'success')
      }
    },

    leaveStatusLabel (leave) {
      if (!this.serverDate) return ''
      if (leave.start_date <= this.serverDate && leave.end_date >= this.serverDate) return 'On Leave'
      if (leave.start_date > this.serverDate) return 'Upcoming'
      return 'Past'
    },

    // ── Staff logout ─────────────────────────────────────
    forceStaffLogout () {
      localStorage.removeItem('staffToken')
      localStorage.removeItem('staffRefreshToken')
      localStorage.removeItem('staffRole')
      this.$router.replace({ name: 'Staff-Login', params: { lang: this.$route.params.lang } })
    },

    async doLogout () {
      const refreshToken = localStorage.getItem('staffRefreshToken')
      if (refreshToken) {
        await staffFetch('/api/admin/logout.php', { method: 'POST', body: JSON.stringify({ refresh_token: refreshToken }) }).catch(() => {})
      }
      localStorage.removeItem('staffToken')
      localStorage.removeItem('staffRefreshToken')
      localStorage.removeItem('staffRole')
      this.$router.push({ name: 'Staff-Login', params: { lang: this.$route.params.lang } })
    }
  }
}
</script>

<style scoped>
.staff-panel {
  min-height: 100vh;
  background: #f0f4fa;
  font-family: 'Open Sans', sans-serif;
}

/* ── Top bar ── */
.sp-topbar {
  background: #1a3c6e;
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 12px rgba(0,0,0,.18);
}
.sp-topbar-inner {
  width: 100%;
  max-width: 960px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.sp-brand { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.sp-brand-mark {
  width: 34px; height: 34px; background: rgba(255,255,255,.15);
  border-radius: 8px; display: flex; align-items: center; justify-content: center;
  font-weight: 800; color: #fff; font-size: 16px;
}
.sp-brand-text b { display: block; color: #fff; font-size: 15px; line-height: 1.2; }
.sp-brand-text span { color: rgba(255,255,255,.65); font-size: 11px; }
.sp-topbar-crumbs { display: flex; align-items: center; gap: 8px; flex: 1; }
.sp-crumb-link { color: rgba(255,255,255,.7); font-size: 13px; cursor: pointer; }
.sp-crumb-link:hover { color: #fff; }
.sp-crumb-sep { color: rgba(255,255,255,.4); font-size: 10px; }
.sp-crumb-active { color: #fff; font-size: 13px; font-weight: 600; }
.sp-topbar-actions { flex-shrink: 0; }
.sp-logout-btn, .sp-back-btn {
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.25);
  color: #fff; padding: 7px 16px; border-radius: 8px; font-size: 13px;
  cursor: pointer; display: flex; align-items: center; gap: 7px; transition: background .15s;
}
.sp-logout-btn:hover, .sp-back-btn:hover { background: rgba(255,255,255,.2); }

/* ── Container ── */
.sp-container {
  max-width: 960px;
  margin: 0 auto;
  padding: 32px 24px 60px;
}
.sp-container > * {
  animation: sp-section-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
}
.sp-container > *:nth-child(1) { animation-delay: 0s; }
.sp-container > *:nth-child(2) { animation-delay: 0.1s; }
.sp-container > *:nth-child(3) { animation-delay: 0.2s; }
.sp-container > *:nth-child(4) { animation-delay: 0.3s; }
.sp-container > *:nth-child(5) { animation-delay: 0.4s; }

/* ── Welcome banner (staff view) ── */
.sp-welcome {
  display: flex; align-items: center; gap: 20px;
  background: linear-gradient(135deg, #f8f8f8 0%, rgba(26,60,110,0.2) 30%, #eef3fb 55%, rgba(26,60,110,0.2) 80%, #f8f8f8 100%);
  background-size: 300% 300%;
  border-radius: 14px; padding: 28px;
  margin-bottom: 20px; box-shadow: 0 2px 12px rgba(0,0,0,.06);
  animation: sp-section-in 0.5s cubic-bezier(0.22, 1, 0.36, 1) both,
             sp-welcome-wave 30s ease-in-out infinite;
}
.sp-avatar-large {
  width: 64px; height: 64px; background: #1a3c6e; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; font-weight: 700; color: #fff; flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(26, 60, 110, 0.3);
}
.sp-avatar-large--admin { background: #0f4c8a; }
.sp-welcome-text {
  animation: sp-welcome-text-in 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.5s both;
}
.sp-welcome-text h1 { font-size: 22px; font-weight: 700; color: #1e293b; margin: 0 0 6px; }
.sp-role-tag {
  font-size: 13px; color: #64748b; margin: 0; display: flex; align-items: center; gap: 6px;
  animation: sp-role-tag-in 0.6s ease 0.25s both;
}

@keyframes sp-welcome-wave {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes sp-section-in {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; }
  /* NOTE: transform intentionally omitted from 'to'.
     transform: translateY(0) in a fill-mode:both animation creates a permanent
     CSS containing block, which breaks position:fixed descendants (ap-si-menu).
     Omitting it lets the browser interpolate from→none and then restore
     transform:none after the animation, so fixed positioning works correctly. */
}
@keyframes sp-welcome-text-in {
  from { opacity: 0; transform: translateX(-12px); }
  to   { opacity: 1; }
}
@keyframes sp-role-tag-in {
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; }
}

/* ── Admin banner ── */
.sp-admin-banner {
  display: flex; align-items: flex-start; gap: 20px;
  background: #fff; border-radius: 14px; padding: 28px;
  margin-bottom: 20px; box-shadow: 0 2px 12px rgba(0,0,0,.06);
  background: linear-gradient(19deg, rgb(248, 248, 248) 0%, rgb(26 60 110 / 20%) 100%);
}
.sp-admin-banner-info h1 { font-size: 22px; font-weight: 700; color: #1e293b; margin: 0 0 8px; }
.sp-admin-banner-meta { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 12px; font-size: 13px; color: #64748b; }
.sp-admin-banner-meta svg { margin-right: 4px; }
.sp-admin-task-stats { display: flex; gap: 8px; flex-wrap: wrap; }
.sp-stat-pill {
  display: inline-block; padding: 3px 12px; border-radius: 20px;
  font-size: 12px; font-weight: 600;
}
.sp-stat-pill--total  { background: #e2e8f0; color: #475569; }
.sp-stat-pill--active { background: #dbeafe; color: #1d4ed8; }
.sp-stat-pill--closed { background: #f1f5f9; color: #94a3b8; }

/* ── Cards ── */
.sp-card {
  background: #fff; border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,.06); margin-bottom: 20px;
}
.sp-card-header {
  padding: 16px 24px; font-size: 14px; font-weight: 600; color: #1e293b;
  border-bottom: 1px solid #f1f5f9; display: flex; align-items: center;
  gap: 8px; background: #f8fafc; border-radius: 14px 14px 0 0;
}
.sp-card-header--toggle {
  cursor: pointer;
  user-select: none;
  transition: background 0.12s;
}
.sp-card-header--toggle:hover { background: #f1f5f9; }
.sp-card--collapsed .sp-card-header { border-bottom: none; border-radius: 14px; }
.sp-card-title { flex: 1; min-width: 0; }
.sp-card-chevron { font-size: 0.75rem; color: #94a3b8; flex-shrink: 0; }
.sp-card-action {
  margin-left: auto; background: #1a3c6e; border: none; color: #fff;
  padding: 5px 14px; border-radius: 7px; font-size: 12px; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
}
.sp-card-action:hover { background: #14305a; }

/* ── Profile grid ── */
.sp-profile-grid {
  display: grid; grid-template-columns: 1fr 1fr; padding: 20px 24px; gap: 0;
}
.sp-profile-item {
  padding: 12px 0; display: flex; flex-direction: column; gap: 4px;
  border-bottom: 1px solid #e5e7eb;
}
.sp-profile-item-username code {
  padding: 4px 12px;
  border-radius: 4px;
  background-color: #e5e7eb;
  color: #374151;
}
/* .sp-profile-item:nth-last-child(-n+1) { border-bottom: none; } */
.sp-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: #333739; }
.sp-value { font-size: 14px; color: #6b7280; font-weight: 500; }
.sp-badge { display: inline-block; padding: 6px 16px; border-radius: 9999px; font-size: 11px; font-weight: 600; text-transform: uppercase; }
.sp-badge--green { background: #dcfce7; color: #15803d; }
.sp-badge--grey  { background: #f1f5f9; color: #64748b; }

/* ── Edit form ── */
.sp-edit-form { padding: 20px 24px; }
.sp-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 16px; }
.sp-field { margin-bottom: 24px; position: relative; }
.sp-field-label { display: block; font-size: 13px; font-weight: 600; color: #1a1a2e; margin-bottom: 6px; }
.sp-field-label-sm { display: block; font-size: 11px; font-weight: 600; color: #1a1a2e; margin-bottom: 4px; }
.sp-req { color: #ef4444; }
.sp-input {
  width: 100%; box-sizing: border-box; padding: 9px 12px;
  border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: 14px;
  color: #1e293b; background: #fff; transition: border-color .15s; outline: none;
}
.sp-input:focus { border-color: #1a3c6e; }
.sp-input--err { border-color: #ef4444; }
.sp-select {
  width: 100%; padding: 9px 12px; border: 1.5px solid #e2e8f0;
  border-radius: 8px; font-size: 14px; color: #1e293b; background: #fff; outline: none;
}
.sp-select--compact { width: auto; min-width: 140px; }
.sp-textarea {
  width: 100%; box-sizing: border-box; padding: 9px 12px;
  border: 1.5px solid #e2e8f0; border-radius: 8px; font-size: 14px;
  color: #1e293b; resize: vertical; outline: none;
}
.sp-textarea:focus { border-color: #1a3c6e; }
.sp-err { display: block; font-size: 12px; color: #ef4444; margin-top: 4px; }
.sp-err-block { margin-bottom: 12px; }
.sp-edit-btn-wrapper {
  padding: 0 24px 24px 24px;
}
.sp-edit-actions { display: flex; gap: 10px; margin-top: 4px; }

/* Shared drive row */
.sp-shared-drive-row { padding: 18px 24px; border-top: 2px solid #d1d5db; }
.sp-shared-drive-edit { display: flex; align-items: center; gap: 10px; }
.sp-drive-icon { color: #1a3c6e; }
.sp-drive-label { font-size: 13px; font-weight: 600; color: #475569; white-space: nowrap; }
.sp-drive-input { flex: 1; }
.sp-drive-btn {
  display: inline-flex; align-items: center; gap: 8px;
  background: #1a3c6e; color: #fff; padding: 9px 18px;
  border-radius: 8px; font-size: 13px; font-weight: 600;
  text-decoration: none; transition: background .15s;
}
.sp-drive-btn:hover { background: #14305a; }

/* ── References ── */
.sp-ref-list { padding: 12px 24px; display: flex; flex-direction: column; gap: 12px; }
.sp-ref-card {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 14px 16px; background: #f1f2f4; border-radius: 10px; gap: 12px;
}
.sp-ref-main { flex: 1; }
.sp-ref-label { font-size: 14px; font-weight: 600; color: #1a3c6e; text-decoration: none; display: flex; align-items: center; gap: 7px; }
.sp-ref-label:hover { color: #0f4c8a; }
.sp-ref-note { font-size: 13px; color: #64748b; margin: 6px 0 0; white-space: pre-wrap; }
.sp-ref-actions { display: flex; gap: 6px; flex-shrink: 0; }
.sp-add-ref-wrapper {
  padding: 0 24px 24px 24px;
}
.sp-add-ref-btn {
  width: 100%; background: #f8fafc; border: 1px dashed #9ca3af; color: #64748b;
  padding: 10px 12px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
}
.sp-add-ref-btn:hover { border-color: #1a3c6e; color: #1a3c6e; background: #f1f5f9; }

/* ── Tasks ── */
.sp-task-tabs { display: flex; gap: 8px; padding: 16px 24px 0; align-items: center; }
.sp-tab-add-btn {
  margin-left: auto; padding: 8px 18px; border: 1.5px solid #dde3ee; border-radius: 9999px;
  background: #fff; color: #64748b; font-size: 13px; font-weight: 500;
  cursor: pointer; display: flex; align-items: center; gap: 7px; transition: all .15s;
}
.sp-tab-add-btn:hover { border-color: #1a3c6e; color: #1a3c6e; }
.sp-task-tab {
  padding: 8px 18px; border: 1.5px solid #dde3ee; border-radius: 9999px;
  background: #fff; color: #64748b; font-size: 13px; font-weight: 500;
  cursor: pointer; display: flex; align-items: center; gap: 7px; transition: all .15s;
}
.sp-task-tab--active { background: #1a3c6e; border-color: #1a3c6e; color: #fff; }
.sp-tab-count {
  background: rgba(255,255,255,.25); border-radius: 10px;
  padding: 1px 7px; font-size: 11px;
}
.sp-task-tab:not(.sp-task-tab--active) .sp-tab-count { background: #f1f5f9; color: #94a3b8; }

.sp-task-list { padding: 16px 24px; display: flex; flex-direction: column; gap: 12px; }
.sp-task-card { border: 1.5px solid #d1d5db; border-radius: 12px; background: #fff; }
.sp-task-card--expanded { border-color: #1a3c6e; }
.sp-task-card-head {
  padding: 18px 24px; display: flex; align-items: center; justify-content: space-between;
  cursor: pointer; gap: 12px; transition: background .12s;
  border-radius: 12px;
}
.sp-task-card--expanded .sp-task-card-head { border-radius: 12px 12px 0 0; background: #f1f5f9; }
.sp-task-card-head:hover { background: #f1f5f9; }
.sp-task-card-left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex: 1; }
.sp-task-card-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.sp-task-client {
  font-size: 14px; font-weight: 600; color: #1e293b;
  display: flex; align-items: center;
}
.sp-task-client::after {
  content: ''; display: inline-block;
  width: 1px; height: 14px; background: #cbd5e1;
  margin-left: 10px; flex-shrink: 0;
}
.sp-task-ya { font-size: 12px; color: #64748b; background: #f1f2f4; padding: 2px 8px; border-radius: 5px; }
.sp-task-services { display: flex; gap: 4px; flex-wrap: wrap; align-items: center; }
.sp-task-services::before {
  content: ''; display: inline-block;
  width: 1px; height: 14px; background: #cbd5e1;
  margin-right: 6px; flex-shrink: 0;
}
.sp-svc-tag { font-size: 11px; background: #eff6ff; color: #1d4ed8; padding: 2px 8px; border-radius: 5px; font-weight: 600; }
.sp-task-toggle-icon { color: #94a3b8; font-size: 12px; }

.sp-status-badge {
  display: inline-block; padding: 6px 16px; border-radius: 9999px;
  font-size: 11px; font-weight: 600; text-transform: uppercase;
}
.sp-status-badge--pending     { background: #fef9c3; color: #854d0e; }
.sp-status-badge--in_progress { background: #dbeafe; color: #1d4ed8; }
.sp-status-badge--closed      { background: #f1f5f9; color: #94a3b8; }

.sp-task-detail { padding: 24px; border-top: 1px solid #1a3c6e; border-radius: 0 0 12px 12px; }
.sp-task-info-grid { display: flex; gap: 24px; flex-wrap: wrap; margin-bottom: 16px; }
.sp-task-info-item { display: flex; flex-direction: column; gap: 3px; }

.sp-section-sub { margin-bottom: 24px; }
.sp-section-sub + .sp-section-sub { border-top: 2px solid #d1d5db; padding-top: 24px; }
.sp-section-sub-title {
  font-size: 12px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .05em; color: #333739; margin-bottom: 10px;
  display: flex; align-items: center; gap: 10px;
}
.sp-add-draft-btn {
  width: 100%; background: #f8fafc; border: 1px dashed #9ca3af; color: #64748b;
  padding: 10px 12px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  margin-top: 12px;
}
.sp-add-draft-btn:hover { border-color: #1a3c6e; color: #1a3c6e; background: #f1f5f9; }
.sp-add-task-wrapper {
  padding: 0 24px 24px 24px;
}
.sp-add-task-btn {
  width: 100%; background: #f8fafc; border: 1px dashed #9ca3af; color: #64748b;
  padding: 18px 24px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
}
.sp-add-task-btn:hover { border-color: #1a3c6e; color: #1a3c6e; background: #f1f5f9; }

.sp-pay-grid { display: flex; flex-direction: column; gap: 6px; }
.sp-pay-row { display: grid; grid-template-columns: 90px 80px 100px 1fr; gap: 8px; font-size: 13px; color: #334155; }
.sp-pay-group { font-weight: 600; color: #64748b; }

/* Draft steps (admin draggable) */
.sp-draft-list { display: flex; flex-direction: column; gap: 12px; }
.sp-draft-step {
  position: relative;
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; background: #f8fafc; border-radius: 8px;
  border: 1px solid #9ca3af;
}
.sp-drag-handle { color: #cbd5e1; cursor: grab; flex-shrink: 0; }
.sp-drag-handle:hover { color: #4b5563; }
.sp-drag-handle:active { cursor: grabbing; }
.sp-draft-info { flex: 1; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.sp-draft-label { font-size: 13px; font-weight: 600; color: #334155; min-width: 120px; }
.sp-draft-controls { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-left: auto; }
.sp-draft-status-wrap ::v-deep .ap-si-trigger {
  padding: 4px 8px; border-radius: 6px; font-size: 12px; font-weight: 600;
  border: 1.5px solid #e2e8f0; background: #fff; min-height: unset; height: auto;
}
.sp-draft-status-wrap ::v-deep .ap-si-val { font-size: 12px; font-weight: 600; padding-right: 20px; }
.sp-draft-status-wrap ::v-deep .ap-si-menu { z-index: 600; font-size: 12px; min-width: 130px; }
.sp-draft-status-wrap ::v-deep .ap-si-opt:hover {
  background: #f1f5f9;
}
.sp-draft-status-wrap ::v-deep .ap-si-opt.is-active {
  background-color: #f1f5f9;
}
.sp-draft-status-wrap--pending     ::v-deep .ap-si-trigger { color: #854d0e; border-color: #fde68a; background: #fef9c3; }
.sp-draft-status-wrap--pending ::v-deep .ap-si-trigger * {
  color: inherit;
}
.sp-draft-status-wrap--in_progress ::v-deep .ap-si-trigger { color: #1d4ed8; border-color: #93c5fd; background: #dbeafe; }
.sp-draft-status-wrap--in_progress ::v-deep .ap-si-trigger * {
  color: inherit;
}
.sp-draft-status-wrap--completed   ::v-deep .ap-si-trigger { color: #15803d; border-color: #86efac; background: #dcfce7; }
.sp-draft-status-wrap--completed ::v-deep .ap-si-trigger * {
  color: inherit;
}
/* .sp-draft-status-wrap--completed ::v-deep .ap-si-val {color:#15803d}
.sp-draft-status-wrap--completed ::v-deep .ap-si-chevron {color:#15803d} */
.sp-draft-status-wrap { position: relative; display: flex; align-items: center; }
.sp-draft-status-wrap--loading { pointer-events: none; opacity: 0.6; }
.sp-draft-status-spinner { position: absolute; right: -18px; font-size: 11px; color: #64748b; }
.sp-draft-date { padding: 4px 8px; border: 1.5px solid #e2e8f0; border-radius: 6px; font-size: 12px; }
.sp-icon-btn.sp-draft-del {border-radius: 100%; padding: 3px 8px; font-size: 11px; position: absolute; top: -10px; right: -10px; }

/* Draft steps (staff read-only) */
.sp-draft-status-dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
}
.sp-draft-status-dot--pending     { background: #fbbf24; }
.sp-draft-status-dot--in_progress { background: #3b82f6; }
.sp-draft-status-dot--completed   { background: #22c55e; }
.sp-draft-status-tag {
  font-size: 11px; font-weight: 600; padding: 6px 16px; border-radius: 9999px;
}
.sp-draft-status-tag--pending     { background: #fef9c3; color: #854d0e; }
.sp-draft-status-tag--in_progress { background: #dbeafe; color: #1d4ed8; }
.sp-draft-status-tag--completed   { background: #dcfce7; color: #15803d; }
.sp-draft-date-val { font-size: 12px; color: #64748b; }
.sp-draft-date-val--empty { color: #cbd5e1; }
.sp-draft-date-wrap { margin-left: auto; }

/* Task actions row */
.sp-task-actions-row {
  display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap;
  padding-top: 28px; border-top: 2px solid #d1d5db; margin-top: 8px;
}
.sp-task-status-group { display: flex; flex-direction: column; gap: 4px; min-width: 140px; }
.sp-task-reassign-group { display: flex; flex-direction: row; align-items: flex-end; gap: 8px; }
.sp-reassign-loading { font-size: 12px; color: #64748b; display: flex; align-items: center; gap: 5px; white-space: nowrap; padding-bottom: 8px; }
.sp-task-status-group ::v-deep .ap-field,
.sp-task-reassign-group ::v-deep .ap-field { margin-bottom: 0; }
.sp-task-btn-group { display: flex; gap: 8px; align-items: center; margin-left: auto; }

/* Buttons */
.sp-btn {
  min-width: 100px; justify-content: center; padding: 9px 18px; border-radius: 9999px; font-size: 13px; font-weight: 600;
  cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 7px; transition: all .15s;
}
.sp-btn-primary { background: #1a3c6e; color: #fff; }
.sp-btn-primary:hover { background: #14305a; }
.sp-btn-primary:disabled { opacity: .6; cursor: not-allowed; }
.sp-btn-ghost { background: transparent; border: 1.5px solid #e2e8f0; color: #64748b; }
.sp-btn-ghost:hover { border-color: #94a3b8; color: #334155; }
.sp-btn-danger { background: #fee2e2; color: #dc2626; }
.sp-btn-danger:hover { background: #fecaca; }
.sp-btn-sm { padding: 6px 12px; font-size: 12px; }
.sp-btn-md { padding: 10px 24px; font-size: 12px; border-radius: 9999px;}
.sp-icon-btn {
  background: #f1f5f9; border: none; color: #64748b; padding: 6px 10px;
  border-radius: 7px; cursor: pointer; font-size: 12px; transition: all .15s;
}
.sp-icon-btn:hover { background: #e2e8f0; color: #334155; }
.sp-icon-btn--danger { background: #fee2e2; color: #dc2626; }
.sp-icon-btn--danger:hover { background: #fecaca; }

/* ── Task form ── */
.sp-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0 16px; }
.sp-search-wrap { position: relative; }
.sp-search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 13px; }
.sp-search-input { padding-left: 34px; }
.sp-search-clear {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  color: #94a3b8; cursor: pointer; font-size: 13px;
}
.sp-dropdown {
  position: absolute; z-index: 1200; background: #fff; border: 1.5px solid #e2e8f0;
  border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,.1);
  width: 100%; max-height: 220px; overflow-y: auto; top: calc(100% + 4px); left: 0;
}
.sp-dropdown--fixed { position: fixed; }
.sp-dropdown-item {
  padding: 10px 14px; cursor: pointer; font-size: 14px; color: #334155;
  display: flex; align-items: center; justify-content: space-between;
}
.sp-dropdown-item:hover { background: #f1f5f9; }
.sp-dropdown--empty { padding: 10px 14px; color: #94a3b8; font-size: 13px; }
.sp-dd-initial { font-size: 11px; color: #94a3b8; background: #f1f5f9; padding: 2px 6px; border-radius: 4px; }

.sp-checkbox-group { display: flex; flex-direction: row; flex-wrap: wrap; gap: 8px 16px; }
.sp-checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #334155; cursor: pointer; margin-bottom: 0; }

.sp-pct-wrap { display: flex; align-items: center; gap: 10px; }
.sp-pct-preview { font-size: 13px; color: #1a3c6e; font-weight: 600; white-space: nowrap; }

.sp-pay-form-row {
  display: flex; align-items: flex-start; gap: 12px; margin-bottom: 10px;
  padding: 12px; background: #f3f4f6; border-radius: 8px; transition: opacity .2s;
}
.sp-pay-form-row--disabled { opacity: .45; pointer-events: none; }
.sp-pay-group-label { font-size: 12px; font-weight: 700; color: #64748b; min-width: 75px; padding-top: 24px; }
.sp-pay-form-fields { display: flex; gap: 10px; flex: 1; flex-wrap: wrap; }
.sp-pay-field { display: flex; flex-direction: column; gap: 4px; }
.sp-input-sm { width: 110px; }
.sp-input-md { width: 135px; }
.sp-pay-total { margin-top: 6px; font-size: 13px; font-weight: 600; }
.sp-pay-total--ok  { color: #15803d; }
.sp-pay-total--err { color: #dc2626; }

/* ── Modals ── */
/* Side sheet (task form) */
.sp-sheet-overlay {
  position: fixed; inset: 0; background: rgba(15,23,42,.45); z-index: 1000;
}
.sp-sheet {
  position: fixed; top: 0; right: 0; height: 100vh; width: 680px; max-width: 100%;
  background: #fff; box-shadow: -8px 0 40px rgba(0,0,0,.18);
  display: flex; flex-direction: column; overflow-y: auto;
}
.sp-sheet-body { flex: 1; overflow-y: auto; scrollbar-width: none; }
.sp-sheet-body::-webkit-scrollbar { display: none; }
.sp-sheet-footer {
  display: flex; gap: 10px; padding: 16px 36px;
  border-top: 1px solid #e2e8f0; background: #fff; flex-shrink: 0;
}
.sp-sheet-anim-enter-active, .sp-sheet-anim-leave-active { transition: opacity .2s ease; }
.sp-sheet-anim-enter, .sp-sheet-anim-leave-to { opacity: 0; }
.sp-sheet-anim-enter-active .sp-sheet, .sp-sheet-anim-leave-active .sp-sheet { transition: transform .25s ease; }
.sp-sheet-anim-enter .sp-sheet, .sp-sheet-anim-leave-to .sp-sheet { transform: translateX(100%); }

.sp-modal-overlay {
  position: fixed; inset: 0; background: rgba(15,23,42,.45);
  z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px;
}
.sp-modal {
  background: #fff; border-radius: 14px; width: 100%; max-width: 520px;
  box-shadow: 0 24px 60px rgba(0,0,0,.18); max-height: 90vh; overflow-y: auto;
}
.sp-modal--wide { max-width: 780px; }
.sp-modal--sm   { max-width: 380px; }
.sp-modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 36px 0;
}
.sp-modal-head h3 { font-size: 17px; font-weight: 700; color: #1e293b; margin: 0; }
.sp-modal-close { background: none; border: none; color: #94a3b8; font-size: 18px; cursor: pointer; padding: 4px; }
.sp-modal-body { padding: 16px 36px 24px; }
.sp-modal-actions { display: flex; gap: 10px; margin-top: 20px; }

/* ── Misc ── */
.sp-loading {
  text-align: center; padding: 80px 24px; color: #94a3b8; font-size: 16px;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.sp-inner-loading { padding: 32px; text-align: center; color: #94a3b8; }
.sp-empty-state { padding: 32px 24px; text-align: center; color: #94a3b8; font-size: 14px; }

@media (max-width: 640px) {
  .sp-profile-grid { grid-template-columns: 1fr; }
  .sp-profile-item:nth-last-child(-n+2) { border-bottom: 1px solid #f1f5f9; }
  .sp-profile-item:last-child { border-bottom: none; }
  .sp-welcome { flex-direction: column; text-align: center; }
  .sp-admin-banner {
    flex-direction: column;
    background: linear-gradient(45deg, rgb(248, 248, 248) 0%, rgb(114, 111, 104, 0.15) 100%);
  }
  .sp-form-row { grid-template-columns: 1fr; }
  .sp-form-grid { grid-template-columns: 1fr; }
  .sp-pay-form-fields { flex-direction: column; }
  .sp-input-sm { width: 100%; }
}

/* ── Compact DateInput for draft steps ── */
.sp-draft-date-picker ::v-deep .ap-field { margin-bottom: 0; }
.sp-draft-date-picker ::v-deep .ap-field > label { display: none; }
.sp-draft-date-picker ::v-deep .ap-datepicker-trigger {
  height: 30px; padding-top: 0; padding-bottom: 0; font-size: 12px;
  border-radius: 6px; background: #f8fafc; border-color: #e2e8f0;
}
.sp-draft-date-picker ::v-deep .ap-datepicker-trigger.is-open {
  border-color: #1a3c6e;
  box-shadow: 0 0 0 3px rgba(23, 35, 212, 0.15);
}
.sp-draft-date-picker ::v-deep .ap-datepicker-val { font-size: 12px; }
.sp-draft-date-picker ::v-deep .ap-calendar { z-index: 500; min-width: 260px; }
.sp-leave-date--err ::v-deep .ap-datepicker-trigger { border-color: #ef4444; }
/* ── Compact DateInput for add task ── */
.date-field ::v-deep .ap-field {display: flex; flex-direction: column; gap: 4px; }
.date-field ::v-deep .ap-field > label { font-size: 11px; font-weight: 600; color: #1a1a2e; margin-bottom: 4px; }
.date-field ::v-deep .ap-datepicker-trigger { background: #f8fafc; border-color: #e2e8f0; }
.date-field ::v-deep .ap-calendar { z-index: 600; }
.sp-pay-date--disabled { pointer-events: none; }

.sp-select-input ::v-deep .ap-si-trigger:hover {
  border-color: #1a3c6e;
}
.sp-select-input ::v-deep .ap-si-trigger.is-open {
  border-color: #1a3c6e;
  box-shadow: 0 0 0 3px rgba(23, 35, 212, 0.15);
}
.sp-select-input ::v-deep .ap-si-opt:hover {
  background: #f1f5f9;
}
.sp-select-input ::v-deep .ap-si-opt.is-active {
  background-color: #f1f5f9;
}

/* ── Leave Banner ── */
.sp-leave-banner {
  display: flex; align-items: flex-start; gap: 16px;
  background: linear-gradient(135deg, #fff7ed, #fef3c7);
  border: 2px solid #f59e0b; border-radius: 14px;
  padding: 20px 24px; margin-bottom: 20px;
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.18);
}
.sp-leave-banner-icon {
  width: 44px; height: 44px; background: #f59e0b; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 18px; flex-shrink: 0;
}
.sp-leave-banner-body { display: flex; flex-direction: column; gap: 8px; }
.sp-leave-banner-title {
  font-size: 15px; font-weight: 700; color: #92400e; letter-spacing: -.01em;
}
.sp-leave-banner-item {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.sp-leave-banner-chip {
  display: inline-block; padding: 2px 9px; border-radius: 20px;
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; flex-shrink: 0;
}
.sp-leave-banner-chip--active   { background: #f59e0b; color: #fff; }
.sp-leave-banner-chip--upcoming { background: #dbeafe; color: #1d4ed8; border: 1px solid #93c5fd; }
.sp-leave-banner-dates {
  font-size: 14px; color: #78350f; font-weight: 600;
}
.sp-leave-banner-note {
  font-size: 12px; color: #92400e; background: rgba(245,158,11,.15);
  padding: 2px 8px; border-radius: 5px;
}

/* ── Leave form ── */
.sp-leave-form-wrap {
  padding: 20px 24px; border-bottom: 1px solid #f1f5f9;
}
.sp-leave-form-hint {
  font-size: 13px; color: #64748b; margin: 0 0 16px;
}
.sp-leave-date-row {
  display: flex; align-items: flex-start; gap: 12px; margin-bottom: 4px;
}
.sp-leave-date-row .sp-field { flex: 1; margin-bottom: 0; }
.sp-leave-date-sep {
  padding-top: 30px; color: #94a3b8; font-size: 13px; flex-shrink: 0;
}
.sp-leave-form-actions { margin-top: 12px; }

/* ── Leave Tabs ── */
.sp-leave-tabs { display: flex; gap: 8px; padding: 16px 24px 0; }

/* ── Leave listing ── */
.sp-leave-list { padding: 12px 24px; display: flex; flex-direction: column; gap: 8px; }
.sp-leave-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; background: #f8fafc; border-radius: 10px; gap: 12px;
}
.sp-leave-row-left { display: flex; align-items: center; gap: 12px; flex: 1; }
.sp-leave-row-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.sp-leave-row-info { display: flex; flex-direction: column; gap: 2px; }
.sp-leave-row-dates { font-size: 14px; font-weight: 600; color: #1e293b; }
.sp-leave-row-note { font-size: 12px; color: #64748b; }

.sp-leave-status-dot {
  width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
}
.sp-leave-status-dot--on-leave { background: #f59e0b; }
.sp-leave-status-dot--upcoming  { background: #3b82f6; }
.sp-leave-status-dot--past      { background: #cbd5e1; }

.sp-leave-chip {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .03em;
}
.sp-leave-chip--on-leave { background: #fef9c3; color: #92400e; border: 1px solid #fde68a; }
.sp-leave-chip--upcoming  { background: #dbeafe; color: #1d4ed8; border: 1px solid #93c5fd; }
.sp-leave-chip--past      { background: #f1f5f9; color: #94a3b8; border: 1px solid #e2e8f0; }

@media (max-width: 640px) {
  .sp-leave-date-row { flex-direction: column; }
  .sp-leave-date-sep { display: none; }
}
</style>
